import { Inject, Injectable } from "@angular/core";
import type { Guid } from "@kiraah-shared/common";
import { Observable, delay, of, throwError } from "rxjs";
import { MOBILE_GATEWAY_URL } from "../../auth/mobile-auth.config";
import type {
  IMobileVoiceReportProcessResult,
  IMobileVoiceReportTaskDraft,
  IMobileVoiceReportValidatePayload,
} from "../models/mobile-voice-report.models";

/**
 * Mock temporaire pour tester le parcours mobile (enregistrement → validation).
 * Passer à `false` et implémenter les POST HTTP dès que le backend Mamadi est prêt.
 */
const USE_VOICE_REPORT_API_MOCK = true;

const MOCK_PROCESS_DELAY_MS = 1500;
const MOCK_VALIDATE_DELAY_MS = 800;

const MOCK_REPORT_HTML = `
<p><strong style="color:#5a2f6b">Compte rendu de chantier</strong></p>
<p>Intervention du jour sur le site. Les points suivants ont été abordés lors de la dictée :</p>
<ul>
  <li>Avancement des travaux de gros œuvre conforme au planning.</li>
  <li>Point sécurité rappelé à l'équipe (EPI obligatoires).</li>
  <li>Matériaux livrés en matinée, stockage conforme.</li>
</ul>
<p><em style="color:#7a6b8a">Document généré automatiquement — à valider ou modifier avant enregistrement.</em></p>
`.trim();

const MOCK_TASKS: IMobileVoiceReportTaskDraft[] = [
  {
    Uid: "mock-task-1",
    Label: "Contrôler la livraison béton",
    Description: "Vérifier la conformité du bon de livraison et la qualité du béton à réception.",
  },
  {
    Uid: "mock-task-2",
    Label: "Planifier la réunion sécurité",
    Description: "Organiser un briefing sécurité avec les sous-traitants présents sur site.",
  },
  {
    Uid: "mock-task-3",
    Label: "Mettre à jour le planning",
    Description: "Ajuster les dates d'intervention suite au retard constaté sur le lot électricité.",
  },
];

/**
 * Client HTTP compte-rendu vocal chantier.
 * Brancher les endpoints réels dans {@link processAudio} et {@link validate} quand le backend est prêt.
 */
@Injectable({ providedIn: "root" })
export class MobileVoiceReportApiClient {
  /** URL de base réservée au branchement futur. */
  readonly baseUrl: string;

  constructor(@Inject(MOBILE_GATEWAY_URL) gatewayUrl: string) {
    this.baseUrl = `${gatewayUrl}/api/consulting/voicereports`;
  }

  /**
   * Envoie l’audio enregistré et retourne le rapport HTML + les tâches proposées.
   * @todo Brancher POST multipart vers le backend (retirer le mock).
   */
  processAudio(_missionUid: Guid, _audio: Blob): Observable<IMobileVoiceReportProcessResult> {
    if (USE_VOICE_REPORT_API_MOCK) {
      return of<IMobileVoiceReportProcessResult>({
        SessionUid: "mock-voice-report-session" as Guid,
        ReportHtml: MOCK_REPORT_HTML,
        Tasks: MOCK_TASKS.map((taskTemplate) => ({ ...taskTemplate })),
      }).pipe(delay(MOCK_PROCESS_DELAY_MS));
    }

    return throwError(() => new Error("mobile.voiceReport.error.apiNotReady"));
  }

  /**
   * Valide et persiste le compte rendu + la liste de tâches.
   * @todo Brancher POST JSON vers le backend (retirer le mock).
   */
  validate(_payload: IMobileVoiceReportValidatePayload): Observable<void> {
    if (USE_VOICE_REPORT_API_MOCK) {
      return of(undefined).pipe(delay(MOCK_VALIDATE_DELAY_MS));
    }

    return throwError(() => new Error("mobile.voiceReport.error.apiNotReady"));
  }
}
